// src/services/approval.ts
import { apiClient } from '../lib/apiClient'
type Json = any

// Database types
type ApprovalRequest = {
  id: string
  applicant_id: string
  type: 'leave' | 'expense' | 'purchase'
  title: string
  status: 'pending' | 'in_progress' | 'approved' | 'rejected'
  details: Json
  created_at: string
  updated_at: string
}
type ApprovalStep = {
  id: string
  request_id: string
  approver_id: string
  level: number
  status: 'pending' | 'approved' | 'rejected'
  comment?: string | null
  processed_at?: string | null
  approver: { id: string; name: string; email: string; position?: { id: string; name: string } | null; department?: { id: string; name: string } | null }
}

// Business logic interfaces
export interface LeaveDetails {
  leave_type: 'annual' | 'sick' | 'personal' | 'other'
  start_date: string
  end_date: string
  total_days: number
  reason?: string
}

export interface ExpenseDetails {
  expense_type: 'transport' | 'meal' | 'accommodation' | 'supplies' | 'other'
  amount: number
  currency: string
  description?: string
  receipt_url?: string
  expense_date: string
}

export interface PurchaseDetails {
  items: Array<{
    name: string
    quantity: number
    unit_price: number
    total_price: number
  }>
  total_amount: number
  currency: string
  vendor: string
  description?: string
}

// Type guards for safe JSON conversion
function isLeaveDetails(details: unknown): details is LeaveDetails {
  if (!details || typeof details !== 'object') return false
  const d = details as any
  return (
    typeof d.leave_type === 'string' &&
    ['annual', 'sick', 'personal', 'other'].includes(d.leave_type) &&
    typeof d.start_date === 'string' &&
    typeof d.end_date === 'string' &&
    typeof d.total_days === 'number'
  )
}

function isExpenseDetails(details: unknown): details is ExpenseDetails {
  if (!details || typeof details !== 'object') return false
  const d = details as any
  return (
    typeof d.expense_type === 'string' &&
    ['transport', 'meal', 'accommodation', 'supplies', 'other'].includes(d.expense_type) &&
    typeof d.amount === 'number' &&
    typeof d.currency === 'string' &&
    typeof d.expense_date === 'string'
  )
}

function isPurchaseDetails(details: unknown): details is PurchaseDetails {
  if (!details || typeof details !== 'object') return false
  const d = details as any
  return (
    Array.isArray(d.items) &&
    typeof d.total_amount === 'number' &&
    typeof d.currency === 'string' &&
    typeof d.vendor === 'string'
  )
}

// Safe JSON to details conversion
function parseDetails<T>(json: Json, type: 'leave' | 'expense' | 'purchase'): T | null {
  try {
    const details = json as unknown
    
    switch (type) {
      case 'leave':
        if (isLeaveDetails(details)) {
          return details as T
        }
        break
      case 'expense':
        if (isExpenseDetails(details)) {
          return details as T
        }
        break
      case 'purchase':
        if (isPurchaseDetails(details)) {
          return details as T
        }
        break
    }
    
    console.error(`Invalid ${type} details:`, json)
    return null
  } catch (error) {
    console.error(`Error parsing ${type} details:`, error)
    return null
  }
}

// Convert details to Json for database
function detailsToJson(details: LeaveDetails | ExpenseDetails | PurchaseDetails): Json {
  return details as Json
}

// Extended types with relations
export interface ApprovalRequestWithRelations extends Omit<ApprovalRequest, 'details'> {
  details: LeaveDetails | ExpenseDetails | PurchaseDetails | null
  applicant?: {
    id: string
    name: string
    email: string
    position?: { id: string; name: string } | null
    department?: { id: string; name: string } | null
    joinDate?: string | null
    totalVacDays?: number | null
    remainingVacDays?: number | null
    vacationLastAccrualDate?: string | null
    vacationAccruedMonths?: number | null
  }
  approval_steps?: ApprovalStep[]
}

export type LeaveRequestWithApplicant = ApprovalRequestWithRelations & { type: 'leave' }

export class ApprovalService {
  // Helper to parse details of list items
  private mapList(items: any[], forcedType?: 'leave' | 'expense' | 'purchase'): ApprovalRequestWithRelations[] {
    return (items || []).map((item) => ({
      ...item,
      details: parseDetails(item.details, forcedType || item.type)
    }))
  }

  // Get all approval requests
  async getApprovalRequests(options?: {
    type?: 'leave' | 'expense' | 'purchase'
    status?: 'pending' | 'in_progress' | 'approved' | 'rejected'
    applicantId?: string
    limit?: number
  }): Promise<ApprovalRequestWithRelations[]> {
    const { data } = await apiClient.get('/api/approval-requests', {
      params: {
        type: options?.type,
        status: options?.status,
        applicantId: options?.applicantId,
        limit: options?.limit
      }
    })
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to load approval requests')
    return this.mapList(data.data, options?.type)
  }

  // Get single approval request by ID
  async getApprovalRequestById(id: string): Promise<ApprovalRequestWithRelations | null> {
    const { data } = await apiClient.get(`/api/approval-requests/${id}`)
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to load approval request')
    const item = data.data
    return { ...item, details: parseDetails(item.details, item.type) }
  }

  // Create leave request
  async createLeaveRequest(
    applicantId: string,
    title: string,
    details: LeaveDetails
  ): Promise<ApprovalRequestWithRelations> {
    const { data } = await apiClient.post('/api/approval-requests', {
      applicantId,
      type: 'leave',
      title,
      details
    })
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to create leave request')
    const item = data.data
    return { ...item, details: parseDetails(item.details, 'leave') }
  }

  // Create expense request
  async createExpenseRequest(
    applicantId: string,
    title: string,
    details: ExpenseDetails
  ): Promise<ApprovalRequestWithRelations> {
    const { data } = await apiClient.post('/api/approval-requests', {
      applicantId,
      type: 'expense',
      title,
      details
    })
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to create expense request')
    const item = data.data
    return { ...item, details: parseDetails(item.details, 'expense') }
  }

  // Update approval request
  async updateApprovalRequest(
    id: string,
    updates: Partial<{
      title: string
      status: 'pending' | 'in_progress' | 'approved' | 'rejected'
      details: LeaveDetails | ExpenseDetails | PurchaseDetails
    }>
  ): Promise<ApprovalRequestWithRelations> {
    const payload: any = {}
    if (updates.title) payload.title = updates.title
    if (updates.status) payload.status = updates.status
    if (updates.details) payload.details = detailsToJson(updates.details)

    const { data } = await apiClient.patch(`/api/approval-requests/${id}`, payload)
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to update request')
    const item = data.data
    return { ...item, details: parseDetails(item.details, item.type) }
  }

  // Delete approval request
  async deleteApprovalRequest(id: string): Promise<void> {
    const { data } = await apiClient.delete(`/api/approval-requests/${id}`)
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to delete request')
  }

  // Approve request
  async approveRequest(
    requestId: string,
    approverId: string,
    comment?: string
  ): Promise<ApprovalRequestWithRelations> {
    const { data } = await apiClient.post(`/api/approval-requests/${requestId}/approve`, { comment })
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to approve request')
    const item = data.data
    return { ...item, details: parseDetails(item.details, item.type) }
  }

  // Reject request
  async rejectRequest(
    requestId: string,
    approverId: string,
    comment: string
  ): Promise<ApprovalRequestWithRelations> {
    const { data } = await apiClient.post(`/api/approval-requests/${requestId}/reject`, { comment })
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to reject request')
    const item = data.data
    return { ...item, details: parseDetails(item.details, item.type) }
  }

  // Get pending approvals for approver
  async getPendingApprovals(approverId: string): Promise<ApprovalRequestWithRelations[]> {
    const { data } = await apiClient.get('/api/approval-requests/pending')
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to load pending approvals')
    return this.mapList(data.data)
  }

  // Get user's vacation stats
  async getUserVacationStats(userId: string): Promise<{
    totalDays: number
    usedDays: number
    remainingDays: number
    pendingDays: number
    lastAccrualDate: string | null
  }> {
    // Get user info
    const [{ data: userRes }, { data: reqRes }] = await Promise.all([
      apiClient.get(`/api/users/${userId}`),
      apiClient.get('/api/approval-requests', { params: { type: 'leave', applicantId: userId } })
    ])
    if (!userRes?.ok) throw new Error(userRes?.error?.message || 'Failed to load user')
    if (!reqRes?.ok) throw new Error(reqRes?.error?.message || 'Failed to load leave requests')

    let usedDays = 0
    let pendingDays = 0

    ;(reqRes.data || []).forEach((request: any) => {
      const leaveDetails = parseDetails<LeaveDetails>(request.details, 'leave')
      if (leaveDetails) {
        if (request.status === 'approved') {
          usedDays += leaveDetails.total_days
        } else if (request.status === 'pending' || request.status === 'in_progress') {
          pendingDays += leaveDetails.total_days
        }
      }
    })

    return {
      totalDays: userRes.data?.total_vac_days || 0,
      usedDays,
      remainingDays: userRes.data?.remaining_vac_days || 0,
      pendingDays,
      lastAccrualDate: userRes.data?.vacation_last_accrual_date || null
    }
  }

  // Get user's expense stats
  async getUserExpenseStats(userId: string): Promise<{
    totalAmount: number
    approvedAmount: number
    pendingAmount: number
    rejectedAmount: number
  }> {
    const { data } = await apiClient.get('/api/approval-requests', { params: { type: 'expense', applicantId: userId } })
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to load expense requests')

    const stats = {
      totalAmount: 0,
      approvedAmount: 0,
      pendingAmount: 0,
      rejectedAmount: 0
    }

    ;(data.data || []).forEach((request: any) => {
      const expenseDetails = parseDetails<ExpenseDetails>(request.details, 'expense')
      if (expenseDetails) {
        stats.totalAmount += expenseDetails.amount
        
        switch (request.status) {
          case 'approved':
            stats.approvedAmount += expenseDetails.amount
            break
          case 'pending':
          case 'in_progress':
            stats.pendingAmount += expenseDetails.amount
            break
          case 'rejected':
            stats.rejectedAmount += expenseDetails.amount
            break
        }
      }
    })

    return stats
  }
}

// Singleton instance
export const approvalService = new ApprovalService()
